import {SpectrumProps} from "./Spectrum";
import React from "react";
import * as d3 from "d3";
import {minDb, nHarmonics} from "./spectra";
import scaleDb from "./scaleDb";
import {HorizontalScale, useChartOptions, VerticalScale} from "./ChartOptions";

export type ThumbnailChartProps = SpectrumProps;

const margin = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
};
const width = 79;
const height = 30;

const xHarmonics = d3.scaleLinear([1, nHarmonics], [margin.left, width - margin.right]);
const xIntervals = d3.scaleLog([1, nHarmonics], [margin.left, width - margin.right]).base(2);
const yIndB = d3.scaleLinear([minDb, 0], [height - margin.bottom, margin.top]).clamp(true);
const yAmplitude = scaleDb([minDb, 0], [height - margin.bottom, margin.top]).clamp(true);

export function ThumbnailChart(props: ThumbnailChartProps) {
    const {horizontalScale, verticalScale} = useChartOptions();

    const chartContent = React.useRef<SVGGElement>(null);

    const xScale = horizontalScale === HorizontalScale.harmonics ? xHarmonics : xIntervals;
    const yScale = verticalScale === VerticalScale.dB ? yIndB : yAmplitude;

    /* This crazy pair of useEffects draws the chart with no transition if the harmonic array is replaced,
     * or with a transition if the scales/axes are replaced
     */
    React.useEffect(() => {
        const x = (_d: number, i: number) => (xScale(i + 1)!);
        /* draw the chart bars */
        d3.select(chartContent.current)
            .selectAll("line.bar")
            /* need to cast away readonly */
            .data(props.harmonics as number[])
            .join("line")
            .attr("class", "bar")
            .attr("width", 1)
            .attr("x1", x)
            .attr("x2", x)
            .attr("y1", height - margin.bottom)
            .attr("y2", (d: number) => yScale(d)!)
        /* don't use xScale and yScale as deps, they are handled by the useEffect below */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.harmonics]);

    React.useEffect(() => {
        const x = (_d: number, i: number) => (xScale(i + 1)!);
        /* redraw the chart bars, with a transition */
        d3.select(chartContent.current)
            /* these type args would be inferred from the .data call */
            .selectAll<SVGGElement, number>("line.bar")
            .join("line")
            /* this is the different bit */
            .transition()
            .attr("x1", x)
            .attr("x2", x)
            .attr("y1", height - margin.bottom)
            .attr("y2", (d: number) => yScale(d)!)
    }, [xScale, yScale]);

    return <svg className="spectrum thumbnail" viewBox={`0 0 ${width} ${height}`}>
        <rect width="100%" height="100%" fill="#333333" />
        <g ref={chartContent}
           pointerEvents="none">
        </g>
    </svg>;
}